import type { AuthenticatedConnectedClient, ConnectedClient } from "../disko/user-toolbar/schemas";
import type { AccountRowJs, AxShareConfigModel, SsoResponse, UserProfileJs, UserSuggestion } from "@/generated/models";
import { trimEmailDomain } from "@/common/lib/string";
import { hashPassword, simpleHash } from "@/common/utils";

export interface JoinAxureData {
  workspaceId: string;
  workspaceName: string;
  userEmail: string;
  organizationId: string;
  organizationName: string;
}

export interface UserProfile {
  userId: string;
  email: string;
  profileImageUrl?: string;
  nickname?: string;
  isAnonymous?: boolean;
  color?: string;
}

export interface ChangeAccountInfoModel {
  currentEmail?: string;
  currentPassword: string;
  newEmail?: string;
  newPassword?: string;
}
export interface SignUpModel {
  email: string;
  password: string;
  callerId?: string;
  doNotSendWelcomeEmail?: boolean;
  allowForSubInstances?: boolean;
  redirect?: string;
}

export interface ResetPasswordModel {
  email: string;
  newPassword: string;
  token: string;
  target: string;
  redirect: string;
}

export function fromUserProfileJs(user: UserProfileJs): UserProfile {
  return {
    userId: user.userId,
    email: user.userEmail,
    nickname: user.username,
    profileImageUrl: user.profileImg,
  };
}

export function fromUserSuggestion(user: UserSuggestion): UserProfile {
  return {
    userId: user.UserId,
    email: user.UserEmail,
    nickname: user.UserName,
    profileImageUrl: user.UserProfileImgUrl,
  };
}

export function fromConnectedAnonymousClient(client: ConnectedClient): UserProfile {
  return {
    userId: "",
    email: "",
    nickname: client.nickname ?? undefined,
    profileImageUrl: client.profileImageUrl ?? undefined,
    isAnonymous: true,
    color: client.color,
  };
}

export function fromConnectedClient(client: AuthenticatedConnectedClient): UserProfile {
  return {
    userId: client.userId,
    email: client.email,
    nickname: client.nickname ?? undefined,
    profileImageUrl: client.profileImageUrl ?? undefined,
    color: client.color,
  };
}

export function fromAccountRowJs(user: AccountRowJs): UserProfile {
  return {
    userId: user.Id,
    email: user.Email,
    nickname: user.Nickname,
    profileImageUrl: user.ProfileImageUrl,
  };
}

export function fromSsoResponse(user: SsoResponse): UserProfile {
  return {
    userId: user.userId,
    email: user.userEmail,
    nickname: user.nickname,
    profileImageUrl: user.profileImageUrl,
  };
}

export function getUserName(email: string, nickname?: string) {
  if (nickname) return nickname;
  return trimEmailDomain(email);
}

const avatarColors = [
  "#e72525",
  "#7eb919",
  "#8d29c9",
  "#37465e",
  "#e48810",
  "#2c70f6",
  "#f70077",
];

export function getAvatarRandomColor() {
  const index = Math.floor(Math.random() * avatarColors.length);
  return avatarColors[index];
}

export function getUserInitials(name: string) {
  return name.trim().substring(0, 1).toUpperCase();
}

export function getAvatarBackgroundColor(name: string) {
  const hash = simpleHash(name);
  const index = Math.abs(hash % avatarColors.length);
  return avatarColors[index];
}

interface LoginPayloadOptions {
  email: string;
  password: string;
  config: AxShareConfigModel;
  includeTokenInResponse?: boolean;
  redirect?: string;
}

interface DoLoginModel {
  loginEmail: string;
  loginPassword: string;
  altPassword: string;
  staySignedIn: boolean;
  clearPass: boolean;
  passwordBlank: boolean;
  accountService: boolean;
  redirect?: string;
  rpRequestId?: string;
}

export async function getLoginPayload(options: LoginPayloadOptions) {
  const { email, password, config, includeTokenInResponse = false, redirect } = options;

  const onPremMode = config.AxShareOnPrem;
  const onPremLdapAuthentication = config.LdapAuthenticationEnabled;
  const useClearPassword = onPremLdapAuthentication;
  const loginPassword = onPremMode && useClearPassword ? password : await hashPassword(password);
  const passwordBlank = !password;
  const altPassword = onPremLdapAuthentication ? password : "";

  const model: DoLoginModel = {
    loginEmail: email.trim(),
    loginPassword,
    altPassword,
    staySignedIn: true,
    clearPass: useClearPassword,
    // accountService is a boolean flag indicating that server should return authToken instead of redirecting with auth code
    accountService: includeTokenInResponse,
    passwordBlank,
    redirect,
  };

  return model;
}
